import { useCallback, useState } from "react";

import { useTranslation } from "next-i18next";

import Icon from "@components/Icon";
import { useMessage } from "@components/Message";
import useNonce from "@hooks/useNonce";
import scrollToTheTop from "@lib/scrollToTheTop";
import debounce from "@utils/debounce";

import { OrderFields, ORDER_MESSAGE_ZONE } from "../../../constants";
import loadOffers from "../../../services/loadOffers";
import type { TCoupon, TOffer, TPackageInfoFieldErrors } from "../../../types";
import useOrderFieldsLoading from "../../useOrderFieldsLoading";

const debouncedOffersLoading = debounce((func) => func(), 200);

export type TUpdateOffersParams = {
  countryFrom?: string;
  countryTo?: string;
  deliveryMethod?: string;
  packageSizeTitle?: string;
  promoCode?: string;
  senderZipCode?: string;
  receiverZipCode?: string;
  affiliateId?: string;
  shipmentId?: string;
  senderEmail?: string;
  senderPhoneNumber?: string;
  offerGroupId?: string;
  useCr?: boolean;
};

type TUpdateOffersData = {
  offers: TOffer[];
  marketingConsent: boolean | null;
  coupon: TCoupon | null;
  errorMessage: string | null;
  fieldErrors: TPackageInfoFieldErrors | null;
};

type TUpdateOffersReturn = {
  offers: TOffer[];
  marketingConsent: boolean | null;
  coupon: TCoupon | null;
  errorMessage: string | null;
  fieldErrors: TPackageInfoFieldErrors | null;
  updateOffers: (params: TUpdateOffersParams) => void;
};

const useUpdateOffers = (initialOffers: TOffer[], isPaymentFailed: boolean): TUpdateOffersReturn => {
  const { t } = useTranslation();
  const setMessage = useMessage();
  const { setFieldLoading, isFieldLoading } = useOrderFieldsLoading();

  const isPackageSizesLoading = isFieldLoading(OrderFields.PACKAGE_SIZE);

  const [{ offers, coupon, errorMessage, fieldErrors, marketingConsent }, setData] = useState<TUpdateOffersData>({
    offers: initialOffers,
    coupon: null,
    marketingConsent: null,
    errorMessage: null,
    fieldErrors: null,
  });

  const { updateNonce, getCurrentNonce } = useNonce();

  const updateOffers = useCallback(
    ({
      countryFrom,
      countryTo,
      deliveryMethod,
      packageSizeTitle,
      promoCode,
      senderZipCode,
      receiverZipCode,
      affiliateId,
      shipmentId,
      senderEmail,
      senderPhoneNumber,
      offerGroupId,
      useCr,
    }: TUpdateOffersParams) => {
      const localNonce = updateNonce();

      if (!countryFrom || !countryTo || !packageSizeTitle) {
        setData((oldData) => ({ ...oldData, offers: [] }));
        setFieldLoading(OrderFields.DELIVERY_METHOD, false);
        setFieldLoading(OrderFields.PROMO_CODE, false);
        setFieldLoading(OrderFields.OFFER_GROUP_ID, false);
        return;
      }

      setFieldLoading(OrderFields.DELIVERY_METHOD, true);
      setFieldLoading(OrderFields.OFFER_GROUP_ID, true);
      setFieldLoading(OrderFields.PROMO_CODE, !!promoCode);

      // If promoCode is removing, clearing coupon early
      if (!promoCode) {
        setData((data) => ({ ...data, marketingConsent, coupon: null }));
      }
      debouncedOffersLoading(async () => {
        if (localNonce !== getCurrentNonce()) {
          return;
        }
        if (isPackageSizesLoading) {
          return;
        }

        const {
          isSuccess,
          offers: newOffers,
          coupon: newCoupon,
          marketingConsent: marketingConsent,
          errorMessage: newErrorMessage,
          fieldErrors: newFieldErrors,
        } = await loadOffers({
          countryFrom,
          countryTo,
          packageSizeTitle,
          promoCode,
          senderZipCode,
          receiverZipCode,
          affiliateId,
          shipmentId,
          senderEmail,
          senderPhoneNumber,
          offerGroupId,
          deliveryMethod,
          useCr,
        });

        setData({
          offers: newOffers || [],
          coupon: newCoupon || null,
          marketingConsent: (marketingConsent === true || marketingConsent === false) ? marketingConsent : null ,
          errorMessage: newErrorMessage || null,
          fieldErrors: newFieldErrors || null,
        });

        setFieldLoading(OrderFields.DELIVERY_METHOD, false);
        setFieldLoading(OrderFields.OFFER_GROUP_ID, false);
        setFieldLoading(OrderFields.PROMO_CODE, false);

        const selectedOffer = newOffers?.find(
          ({ deliveryMethod: offerDeliveryMethod }) => offerDeliveryMethod === deliveryMethod
        );

        if (!isSuccess) {
          setMessage({
            message: t("order-message-failed_to_load_offers"),
            props: {
              variant: "error",
              icon: <Icon icon="warning" size="1.45rem" />,
              canClose: true,
              padding: "0.45rem 0.75rem",
            },
            zone: ORDER_MESSAGE_ZONE,
          });
          scrollToTheTop();
          return;
        }

        if (!isPaymentFailed && newOffers && newOffers.length > 0 && selectedOffer) {
          setMessage({
            message: null,
            props: {},
            zone: ORDER_MESSAGE_ZONE,
          });
        }

        if (!newOffers?.length) {
          setMessage({
            message: t("order-message-no_offers_available"),
            props: {
              variant: "error",
              icon: <Icon icon="warning" size="1.45rem" />,
              canClose: true,
              padding: "0.45rem 0.75rem",
            },
            zone: ORDER_MESSAGE_ZONE,
          });
          scrollToTheTop();
        }
      });
    },
    [updateNonce, setFieldLoading, getCurrentNonce, isPackageSizesLoading, setMessage, t]
  );

  return { offers, coupon, errorMessage, fieldErrors, updateOffers, marketingConsent };
};
export default useUpdateOffers;
